import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  AppBar,
  Toolbar,
  Chip,
  Fade,
  Avatar,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton
} from '@mui/material';
import {
  Psychology,
  TrendingUp,
  Timer,
  EmojiEvents,
  Star,
  AutoAwesome,
  Lightbulb,
  Rocket,
  SelfImprovement,
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowForward,
  Shield,
  FlashOn,
  Groups,
  AccessTime,
  TrendingUpSharp
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// Enhanced animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(159, 186, 143, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(159, 186, 143, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(159, 186, 143, 0); }
`;

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled components
const GradientContainer = styled(Box)(({ theme }) => ({
  background: `
    linear-gradient(135deg, 
      #9FBA8F 0%,     /* Sage green */
      #A8A4D9 25%,    /* Lavender */
      #8FA67F 50%,    /* Deeper sage */
      #B5B1E3 75%,    /* Light lavender */
      #9FBA8F 100%    /* Back to sage */
    )`,
  backgroundSize: '400% 400%',
  animation: `${gradient} 20s ease infinite`, // Slower, more calming
  minHeight: '100vh',
  position: 'relative',
  overflow: 'auto',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 80%, rgba(159, 186, 143, 0.2) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(168, 164, 217, 0.2) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(159, 186, 143, 0.15) 0%, transparent 50%)
    `,
    zIndex: 1,
  }
}));

const FloatingElement = styled(Box)(({ delay = 0 }) => ({
  animation: `${float} 6s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  position: 'absolute',
  zIndex: 2,
}));

const GlassmorphismCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '24px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 48px 0 rgba(31, 38, 135, 0.3)',
    background: 'rgba(255, 255, 255, 0.35)',
  }
}));

const PulseButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '50px',
  padding: variant === 'primary' ? '16px 48px' : '14px 32px',
  fontSize: variant === 'primary' ? '18px' : '16px',
  fontWeight: 700,
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  ...(variant === 'primary' && {
    background: 'linear-gradient(45deg, #4bcf7f, #00f2fe, #38a3ff)',
    backgroundSize: '200% 200%',
    animation: `${gradient} 3s ease infinite`,
    color: 'white',
    border: 'none',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      animation: `${pulse} 1.5s infinite, ${gradient} 3s ease infinite`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
      transition: 'left 0.5s',
    },
    '&:hover::before': {
      left: '100%',
    }
  }),
  ...(variant === 'secondary' && {
    background: 'rgba(255, 255, 255, 0.95)',
    color: '#1a1a1a',
    border: '2px solid transparent',
    backdropFilter: 'blur(10px)',
    fontWeight: 700,
    textShadow: 'none',
    '&:hover': {
      background: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      border: '2px solid #4bcf7f',
    }
  })
}));

const StatsCard = styled(GlassmorphismCard)(({ theme }) => ({
  textAlign: 'center',
  padding: '24px 16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

const FeatureCard = styled(GlassmorphismCard)(({ theme }) => ({
  padding: '32px 24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
}));

const TestimonialCard = styled(GlassmorphismCard)(({ theme }) => ({
  padding: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: '0 25px 50px rgba(0,0,0,0.3), 0 15px 25px rgba(0,0,0,0.2)',
  background: 'rgba(255,255,255,0.08)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.15)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(75,207,127,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    zIndex: 1,
    pointerEvents: 'none'
  },

  '& video': {
    width: '100%',
    height: 'auto',
    display: 'block',
    position: 'relative',
    zIndex: 2,
    borderRadius: '24px'
  },

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 35px 70px rgba(0,0,0,0.4), 0 20px 35px rgba(0,0,0,0.3)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
  },

  [theme.breakpoints.down('md')]: {
    borderRadius: '20px',
    
    '& video': {
      borderRadius: '20px'
    }
  }
}));

const LandingPage = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSection, setVisibleSection] = useState('hero');
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [headlineVisible, setHeadlineVisible] = useState(true);

  // Cycling headlines array
  const headlines = [
    { text: "Rebuild Your Peace. One Rep at a Time.", powerWord: "Peace" },
    { text: "Train Your Mind Like It Matters—Because It Does.", powerWord: "Mind" },
    { text: "The World Trains Your Stress. We Train Your Sanity.", powerWord: "Sanity" },
    { text: "Your Peace Deserves the Same Discipline as Your Grind.", powerWord: "Discipline" },
    { text: "Reps for Your Sanity. Not Your Stress.", powerWord: "Sanity" },
    { text: "Because Self-Care Should Feel Intelligent.", powerWord: "Intelligent" }
  ];

  // Redirect authenticated users
  useEffect(() => {
    if (session?.user) {
      navigate('/conversation');
    }
  }, [session, navigate]);

  // Cycling headline effect
  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineVisible(false);
      
      setTimeout(() => {
        setCurrentHeadlineIndex((prevIndex) => 
          (prevIndex + 1) % headlines.length
        );
        setHeadlineVisible(true);
      }, 500); // Fade out duration
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [headlines.length]);

  // Helper function to render headline with highlighted power word
  const renderHeadlineWithPowerWord = (headline) => {
    const { text, powerWord } = headline;
    const parts = text.split(powerWord);
    
    return (
      <>
        {parts[0]}
        <Box component="span" sx={{ color: '#ffffff' }}>
          {powerWord}
        </Box>
        {parts[1]}
      </>
    );
  };

  // Add Crisp chatbox
  useEffect(() => {
    // Check if Crisp is already loaded to prevent conflicts
    if (window.$crisp && document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      return;
    }

    window.$crisp = [];
    window.CRISP_WEBSITE_ID = "d457b4c6-e683-4db4-836a-0e3aebc840d9";
    
    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // More thorough cleanup on unmount
      const existingScript = document.querySelector('script[src="https://client.crisp.chat/l.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
      
      // Clean up Crisp variables
      delete window.$crisp;
      delete window.CRISP_WEBSITE_ID;
      
      // Remove any Crisp-related DOM elements
      const crispElements = document.querySelectorAll('[id*="crisp"], [class*="crisp"]');
      crispElements.forEach(element => {
        element.remove();
      });
    };
  }, []);

  // Navigation handlers
  const handleGetStarted = () => {
    navigate('/login?mode=signup');
  };

  const handleLogin = () => {
    navigate('/login?mode=login');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    const sections = ['hero', 'features', 'stats', 'testimonials'];
    sections.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <GradientContainer>
      {/* Floating decorative elements */}
      <FloatingElement delay={0} sx={{ top: '10%', left: '5%', zIndex: 1 }}>
        <Psychology sx={{ fontSize: 60, color: 'rgba(255,255,255,0.1)' }} />
      </FloatingElement>
      <FloatingElement delay={2} sx={{ top: '20%', right: '10%', zIndex: 1 }}>
        <AutoAwesome sx={{ fontSize: 80, color: 'rgba(255,255,255,0.1)' }} />
      </FloatingElement>
      <FloatingElement delay={4} sx={{ bottom: '20%', left: '10%', zIndex: 1 }}>
        <Rocket sx={{ fontSize: 70, color: 'rgba(255,255,255,0.1)' }} />
      </FloatingElement>

      {/* Navigation Header */}
      <AppBar 
        position="fixed" 
        sx={{ 
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src="/sanity_logo.png" 
              alt="Sanity Reps Logo" 
              style={{ height: '40px', marginRight: '12px' }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: 'white',
                textShadow: '1px 1px 0px #ccc, -1px -1px 0px #ccc, 1px -1px 0px #ccc, -1px 1px 0px #ccc, 0.5px 0.5px 0px #ccc, -0.5px -0.5px 0px #ccc, 0.5px -0.5px 0px #ccc, -0.5px 0.5px 0px #ccc',
                fontSize: { xs: '20px', md: '24px' }
              }}
            >
              Sanity Reps
            </Typography>
          </Box>
          
          {!isMobile ? (
            <Stack direction="row" spacing={2}>
              <Button 
                color="inherit" 
                onClick={() => scrollToSection('features')}
                sx={{ color: 'white', fontWeight: 500 }}
              >
                Features
              </Button>
              <Button 
                color="inherit" 
                onClick={() => scrollToSection('testimonials')}
                sx={{ color: 'white', fontWeight: 500 }}
              >
                Reviews
              </Button>
              <Button 
                onClick={handleLogin}
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  '&:hover': { background: 'rgba(255,255,255,0.1)' }
                }}
              >
                Login
              </Button>
              <PulseButton variant="primary" onClick={handleGetStarted}>
                Get Started Free
              </PulseButton>
            </Stack>
          ) : (
            <IconButton 
              color="inherit" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ color: 'white' }}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
        </Toolbar>
        
        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <Box 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px)',
              p: 2 
            }}
          >
            <Stack spacing={2}>
              <Button 
                fullWidth 
                onClick={() => scrollToSection('features')}
                sx={{ color: 'white', justifyContent: 'flex-start' }}
              >
                Features
              </Button>
              <Button 
                fullWidth 
                onClick={() => scrollToSection('testimonials')}
                sx={{ color: 'white', justifyContent: 'flex-start' }}
              >
                Reviews
              </Button>
              <Button 
                fullWidth 
                onClick={handleLogin}
                sx={{ color: 'white', justifyContent: 'flex-start' }}
              >
                Login
              </Button>
              <PulseButton 
                fullWidth 
                variant="primary" 
                onClick={handleGetStarted}
                sx={{ mt: 2 }}
              >
                Get Started Free
              </PulseButton>
            </Stack>
          </Box>
        )}
      </AppBar>

      {/* Hero Section */}
      <Box 
        id="hero"
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          pt: { xs: 10, md: 12 },
          position: 'relative',
          zIndex: 2
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12}>
              <Fade in={visibleSection === 'hero'} timeout={1000}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    label="� Trusted for Burnout Recovery Worldwide"
                    sx={{
                      background: 'rgba(159, 186, 143, 0.3)', // Sage green with transparency
                      color: 'white',
                      fontWeight: 600,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(159, 186, 143, 0.4)',
                      mb: 3,
                      animation: `${pulse} 3s infinite`, // Slower, calmer pulse
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      fontSize: '0.9rem'
                    }}
                  />
                  
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
                      fontWeight: 700, // Slightly lighter
                      color: '#ffffff',
                      lineHeight: 1.2,
                      mb: 3,
                      textShadow: '0 6px 12px rgba(0,0,0,0.6), 0 3px 6px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.2)',
                      letterSpacing: '-0.01em',
                      minHeight: { xs: '200px', sm: '280px', md: '360px', lg: '400px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Fade 
                      in={headlineVisible} 
                      timeout={{ enter: 800, exit: 500 }}
                      style={{
                        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: 'center',
                          width: '100%'
                        }}
                      >
                        <Box component="span" sx={{ 
                          color: '#9FBA8F', // Sage green
                          textShadow: '0 6px 12px rgba(0,0,0,0.6), 0 3px 6px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.2)',
                        }}>
                          {renderHeadlineWithPowerWord(headlines[currentHeadlineIndex])}
                        </Box>
                      </Box>
                    </Fade>
                  </Typography>
                  
                  {/* Newspaper Clipping Style Quote */}
                  <Box
                    sx={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      color: '#2c2c2c',
                      padding: '20px 24px',
                      borderRadius: '8px',
                      border: '2px dashed #9FBA8F',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)',
                      fontFamily: '"Times New Roman", Times, serif',
                      fontStyle: 'italic',
                      lineHeight: 1.5,
                      mb: 4,
                      maxWidth: '600px',
                      mx: 'auto',
                      position: 'relative',
                      transform: 'rotate(-0.5deg)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        right: '8px',
                        bottom: '8px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.95rem', md: '1.1rem' },
                        fontWeight: 400,
                        color: '#2c2c2c',
                        mb: 1,
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      "Only 33% of people feel they're thriving in life."
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        color: '#666',
                        fontWeight: 500,
                        mb: 2,
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      — Gallup Global Study
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', md: '1.15rem' },
                        fontWeight: 600,
                        color: '#9FBA8F',
                        fontFamily: '"Inter", "Roboto", sans-serif',
                        fontStyle: 'normal',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      Sanity Reps was built for the other 67%.
                    </Typography>
                  </Box>
                  
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 400, // Softer weight
                      lineHeight: 1.7, // More breathing room
                      mb: 4,
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      textShadow: '0 3px 6px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)',
                      letterSpacing: '0.01em',
                      background: 'rgba(0,0,0,0.15)',
                      padding: '20px 24px', // More padding for breathing room
                      borderRadius: '16px', // Softer corners
                      backdropFilter: 'blur(15px)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      maxWidth: '800px',
                      mx: 'auto'
                    }}
                  >
                    If you're holding too much, feeling constantly drained, or running on empty — 
                    you're not broken. You're human. This isn't therapy. It's recovery for your nervous system.
                  </Typography>
                  
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={3} 
                    sx={{ mb: 4, justifyContent: 'center' }}
                  >
                    <PulseButton 
                      variant="primary" 
                      size="large" 
                      onClick={handleGetStarted}
                      endIcon={<ArrowForward />}
                      sx={{
                        background: 'linear-gradient(45deg, #9FBA8F 30%, #A8A4D9 90%)', // Sage to lavender
                        color: 'white',
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #8FA67F 30%, #9890C9 90%)',
                        }
                      }}
                    >
                      Start My Free Personalized Plan
                    </PulseButton>
                    <PulseButton 
                      variant="secondary" 
                      size="large" 
                      onClick={handleLogin}
                      sx={{
                        color: '#9FBA8F',
                        borderColor: '#9FBA8F',
                        '&:hover': {
                          backgroundColor: 'rgba(159, 186, 143, 0.1)',
                          borderColor: '#9FBA8F'
                        }
                      }}
                    >
                      I Already Have an Account
                    </PulseButton>
                  </Stack>
                  
                  <Stack direction="row" spacing={4} alignItems="center" justifyContent="center">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Shield sx={{ color: '#9FBA8F', mr: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
                      <Typography sx={{ 
                        color: '#ffffff', 
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        No Insurance Required
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FlashOn sx={{ color: '#A8A4D9', mr: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
                      <Typography sx={{ 
                        color: '#ffffff', 
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        No Judgment
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Explainer Video Section */}
      <Box 
        sx={{ 
          py: { xs: 6, md: 10 },
          position: 'relative',
          zIndex: 2
        }}
      >
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                  fontWeight: 600, // Softer weight
                  color: '#ffffff',
                  mb: 2,
                  textShadow: '0 4px 8px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '0em'
                }}
              >
                See How Natural Recovery Works
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  fontWeight: 300, // Very light
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.8, // More breathing room
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                Watch how our approach helps your nervous system find calm, one mindful step at a time
              </Typography>
            </Box>
          </Fade>

          <Fade in={true} timeout={1200} style={{ transitionDelay: '300ms' }}>
            <Box sx={{ 
              maxWidth: { xs: '100%', sm: '90%', md: '80%', lg: '70%' },
              mx: 'auto',
              px: { xs: 0, sm: 2 }
            }}>
              <VideoContainer>
                <video
                  controls
                  preload="metadata"
                  poster="/assets/thumb1.jpeg"
                  style={{
                    width: '100%',
                    height: 'auto',
                    minHeight: '200px'
                  }}
                  onError={(e) => {
                    console.error('Video failed to load:', e);
                    e.target.style.display = 'none';
                    e.target.nextSibling?.style?.setProperty('display', 'flex');
                  }}
                >
                  <source src="https://backend.sanityreps.com/static/videos/explainer1.mp4" type="video/mp4" />
                  <Box
                    sx={{
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '300px',
                      background: 'rgba(0,0,0,0.2)',
                      borderRadius: '24px',
                      color: 'white',
                      textAlign: 'center',
                      p: 4
                    }}
                  >
                    <Typography variant="h6">
                      Video temporarily unavailable. Please refresh to try again.
                    </Typography>
                  </Box>
                </video>
              </VideoContainer>
            </Box>
          </Fade>

          <Fade in={true} timeout={1000} style={{ transitionDelay: '600ms' }}>
            <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
              <PulseButton 
                variant="primary" 
                size="large" 
                onClick={handleGetStarted}
                endIcon={<ArrowForward />}
                sx={{
                  px: { xs: 4, md: 6 },
                  py: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  background: 'linear-gradient(45deg, #9FBA8F 30%, #A8A4D9 90%)',
                  color: 'white',
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #8FA67F 30%, #9890C9 90%)',
                  }
                }}
              >
                Begin Your Healing Journey
              </PulseButton>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box 
        id="stats" 
        sx={{ 
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 2
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 600, // Softer weight
              color: '#ffffff',
              mb: 6,
              fontSize: { xs: '2rem', md: '3rem' },
              textShadow: '0 4px 8px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            A Safe Space for Your Recovery
          </Typography>
          
          <Grid container spacing={4}>
            {[
              { icon: Groups, label: 'Finding Relief', value: 'Thousands', color: '#9FBA8F' },
              { icon: AccessTime, label: 'Stress Reduced', value: 'Daily', color: '#A8A4D9' },
              { icon: TrendingUpSharp, label: 'Feel Better', value: '92%', color: '#9FBA8F' },
              { icon: Star, label: 'Trust Rating', value: '4.8/5', color: '#A8A4D9' }
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Fade 
                  in={visibleSection === 'stats'} 
                  timeout={1000}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <StatsCard>
                    <stat.icon 
                      sx={{ 
                        fontSize: { xs: 40, md: 50 },
                        color: stat.color, // Use the individual stat color
                        mb: 2
                      }} 
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: '#ffffff',
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        mb: 1,
                        textShadow: '0 3px 6px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)'
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'white',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 500,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </StatsCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        id="features" 
        sx={{ 
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 2
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 600, // Softer
              color: '#ffffff',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
              textShadow: '0 4px 8px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Everything You Need to Feel Human Again
          </Typography>
          
          <Typography
            variant="h6"
            align="center"
            sx={{
              color: '#ffffff',
              mb: 8,
              fontSize: { xs: '1rem', md: '1.2rem' },
              fontWeight: 400, // Lighter
              textShadow: '0 2px 4px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.2)',
              opacity: 0.9
            }}
          >
            No insurance. No judgment. Just relief.
          </Typography>
          
          <Grid container spacing={4}>
            {[
              {
                icon: Psychology,
                title: 'Compassionate AI Support',
                description: 'Meet with your personal AI wellness companion. Available 24/7, deeply understanding, and trained in nervous system recovery. Like having a therapist who never sleeps.',
                color: '#9FBA8F' // Sage green
              },
              {
                icon: SelfImprovement,
                title: 'Instant Calm Techniques',
                description: 'Access science-backed breathing exercises, grounding practices, and micro-meditations designed to soothe your overwhelmed nervous system in minutes.',
                color: '#A8A4D9' // Lavender
              },
              {
                icon: TrendingUp,
                title: 'Track Your Healing',
                description: 'See your progress unfold gently. Monitor stress levels, energy patterns, and emotional balance as you rebuild your capacity to thrive.',
                color: '#9FBA8F' // Sage green
              },
              {
                icon: Lightbulb,
                title: 'Daily Nurturing Insights',
                description: 'Receive personalized wisdom that helps you understand your patterns and build sustainable habits for long-term emotional resilience.',
                color: '#A8A4D9' // Lavender
              },
              {
                icon: EmojiEvents,
                title: 'Stay Motivated',
                description: 'Celebrate wins with our achievement system. Small victories add up to life-changing transformation.',
                color: '#00f2fe'
              },
              {
                icon: Timer,
                title: 'Always Available',
                description: 'Get support whenever you need it - 3am anxiety, work stress, relationship issues. We\'re here 24/7.',
                color: '#38a3ff'
              }
            ].map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade 
                  in={visibleSection === 'features'} 
                  timeout={1000}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <FeatureCard>
                    <feature.icon 
                      sx={{ 
                        fontSize: { xs: 50, md: 60 },
                        color: feature.color,
                        mb: 3
                      }} 
                    />
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: '#ffffff',
                        mb: 2,
                        fontSize: { xs: '1.3rem', md: '1.5rem' },
                        textShadow: '0 3px 6px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)'
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#ffffff',
                        lineHeight: 1.6,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 400,
                        textShadow: '0 2px 4px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.2)'
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box 
        id="testimonials" 
        sx={{ 
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 2
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              color: '#ffffff',
              mb: 8,
              fontSize: { xs: '2rem', md: '3rem' },
              textShadow: '0 4px 8px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Real Stories, Real Transformation
          </Typography>
          
          <Grid container spacing={4}>
            {[
              {
                name: 'Sarah Chen',
                role: 'Marketing Manager',
                avatar: '/woman1.png',
                rating: 5,
                text: 'Sanity Reps completely transformed how I handle stress and anxiety. The AI mental wellness sessions feel so personal and understanding. I\'ve never felt more in control of my mental health.'
              },
              {
                name: 'Michael Rodriguez',
                role: 'Software Engineer',
                avatar: '/man1.png',
                rating: 5,
                text: 'As someone who struggled with depression, this app gave me hope again. The daily insights and progress tracking helped me see the light at the end of the tunnel. Truly life-changing.'
              },
              {
                name: 'Emma Thompson',
                role: 'Teacher',
                avatar: '/woman2.png',
                rating: 5,
                text: 'The meditation features are incredible! I use them every morning and evening. My students have even noticed how much calmer and more patient I\'ve become. Thank you, Sanity Reps!'
              }
            ].map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade 
                  in={visibleSection === 'testimonials'} 
                  timeout={1000}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <TestimonialCard>
                    <Box sx={{ display: 'flex', mb: 2 }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} sx={{ color: '#FFC107', fontSize: 20 }} />
                      ))}
                    </Box>
                    
                    <Typography
                      sx={{
                        color: 'white',
                        fontStyle: 'italic',
                        lineHeight: 1.6,
                        mb: 3,
                        flexGrow: 1,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 400,
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                      }}
                    >
                      {testimonial.text}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={testimonial.avatar}
                        sx={{ 
                          width: 50, 
                          height: 50,
                          mr: 2,
                          border: '2px solid rgba(255,255,255,0.3)'
                        }} 
                      />
                      <Box>
                        <Typography
                          sx={{
                            color: 'white',
                            fontWeight: 700,
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                          }}
                        >
                          {testimonial.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: { xs: '0.8rem', md: '0.9rem' },
                            fontWeight: 500,
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                          }}
                        >
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                  </TestimonialCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 2
        }}
      >
        <Container maxWidth="md">
          <GlassmorphismCard sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#ffffff',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' },
                textShadow: '0 4px 8px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Don't Wait Another Day to Feel Better
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                color: '#ffffff',
                mb: 4,
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.2rem' },
                fontWeight: 500,
                textShadow: '0 2px 4px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              Thousands of people have already started their journey. Join them today and start 
              seeing results in your first session. You deserve to feel amazing.
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={3} 
              justifyContent="center"
              sx={{ mb: 3 }}
            >
              <PulseButton 
                variant="primary" 
                size="large" 
                onClick={handleGetStarted}
                endIcon={<Rocket />}
              >
                Start Feeling Better Now - Free
              </PulseButton>
              <PulseButton 
                variant="secondary" 
                size="large" 
                onClick={handleLogin}
              >
                I Already Have An Account
              </PulseButton>
            </Stack>
            
            <Typography
              sx={{
                color: '#ffffff',
                fontSize: '0.9rem',
                mb: 2,
                fontWeight: 500,
                textShadow: '0 2px 4px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              ✓ Free forever plan available • ✓ Start in 30 seconds
            </Typography>
          </GlassmorphismCard>
        </Container>
      </Box>

      {/* Final Comfort Message */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 12 },
          position: 'relative',
          zIndex: 2,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Fade in={true} timeout={1000}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                  fontWeight: 500,
                  color: '#ffffff',
                  lineHeight: 1.4,
                  mb: 4,
                  textShadow: '0 4px 8px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                You don't have to hold it all together.
                <br />
                <Box component="span" sx={{ 
                  color: '#9FBA8F',
                  fontWeight: 400
                }}>
                  Just start here.
                </Box>
              </Typography>
              
              <PulseButton 
                variant="primary" 
                size="large" 
                onClick={handleGetStarted}
                sx={{
                  px: { xs: 5, md: 8 },
                  py: { xs: 2, md: 2.5 },
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  background: 'linear-gradient(45deg, #9FBA8F 30%, #A8A4D9 90%)',
                  color: 'white',
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(159, 186, 143, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #8FA67F 30%, #9890C9 90%)',
                    boxShadow: '0 12px 35px rgba(159, 186, 143, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Start My Free Personalized Plan
              </PulseButton>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        sx={{ 
          py: 4,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          position: 'relative',
          zIndex: 2
        }}
      >
        <Container>
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            justifyContent="space-between" 
            alignItems="center"
            spacing={2}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src="/sanity_logo.png" 
                alt="Sanity Reps" 
                style={{ height: '30px', marginRight: '8px' }}
              />
              <Typography 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  fontWeight: 600
                }}
              >
                Sanity Reps
              </Typography>
            </Box>
            
            <Typography 
              sx={{ 
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.9rem'
              }}
            >
              © 2025 Sanity Reps. Compassionate recovery for your nervous system.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </GradientContainer>
  );
};

export default LandingPage;
